import {
  changeVaultPassword,
  createVault,
  decryptVaultArtifact,
  encryptVaultArtifact,
  resetVaultPasswordWithRecoveryCode,
  unlockVaultWithPassword,
  type EncryptedVaultArtifactV1,
  type VaultManifestV1,
  type VaultSession,
} from "@rcprofessionale/vault-core";
import {
  loadEncryptedVaultRevision,
  saveNewVaultDocument,
  type PlaintextVaultArtifactSet,
  type VaultPersistenceRemote,
} from "@rcprofessionale/vault-persistence";

import {
  QUOTE_DOCUMENT_SCHEMA_VERSION,
  type VersionedQuoteDocument,
} from "./versioned-quote.js";

export interface VaultLabDocument {
  readonly documentId: string;
  readonly createdAt: string;
  readonly artifacts: readonly EncryptedVaultArtifactV1[];
}

export interface VaultCloudContext {
  readonly userId: string;
  readonly remote: VaultPersistenceRemote;
}

interface DemoPreview {
  readonly clientLabel: string;
  readonly projectTitle: string;
  readonly total: string;
}

const QUOTE_ARCHIVE_FORMAT = "preventivonow-quote-archive" as const;
const QUOTE_ARCHIVE_VERSION = 1 as const;

export interface ArchivedQuoteSnapshotV1 {
  readonly format: typeof QUOTE_ARCHIVE_FORMAT;
  readonly version: typeof QUOTE_ARCHIVE_VERSION;
  readonly filename: string;
  readonly quote: VersionedQuoteDocument;
}

export interface VaultQuoteArchiveInput {
  readonly pdfBytes: Uint8Array;
  readonly filename: string;
  readonly quote: VersionedQuoteDocument;
}

export interface ArchivedQuotePreview {
  readonly clientLabel: string;
  readonly projectTitle: string;
  readonly total: string;
  readonly filename: string;
  readonly issueDate: string;
  readonly resultSha256: string;
}

export interface DecryptedArchivedQuotePdf {
  readonly bytes: Uint8Array;
  readonly filename: string;
}

export interface VaultLabController {
  setPendingQuote(input: VaultQuoteArchiveInput | null): void;
  setCloudContext(context: VaultCloudContext | null): void;
}

interface VaultLabState {
  manifest: VaultManifestV1 | null;
  session: VaultSession | null;
  recoveryCode: string | null;
  recoveryConfirmed: boolean;
  documents: VaultLabDocument[];
  preview: ArchivedQuotePreview | null;
  selectedDocumentId: string | null;
  pendingQuote: VaultQuoteArchiveInput | null;
  cloud: VaultCloudContext | null;
  remoteRevisions: Map<string, number>;
  message: string | null;
  error: string | null;
  busy: boolean;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function getPassword(form: HTMLFormElement, name: string): string {
  const value = new FormData(form).get(name);
  return typeof value === "string" ? value : "";
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function shortId(value: string): string {
  return value.slice(0, 8).toUpperCase();
}

function renderStatus(root: HTMLElement, state: VaultLabState): void {
  const status = root.querySelector<HTMLElement>("[data-vault-status]");
  const live = root.querySelector<HTMLElement>("[data-vault-live]");
  if (status !== null) {
    status.textContent = state.manifest === null
      ? "Non attivato"
      : state.session === null
        ? "Bloccato"
        : state.recoveryConfirmed
          ? "Sbloccato"
          : "Codice da conservare";
    status.dataset.tone = state.session === null ? "locked" : "unlocked";
  }
  if (live !== null) {
    live.textContent = state.error ?? state.message ?? "";
    live.dataset.tone = state.error === null ? "ok" : "error";
    live.hidden = state.error === null && state.message === null;
  }
}

function baseShell(cloudEnabled: boolean): string {
  return `
    <div class="vault-lab__heading">
      <div>
        <span class="eyebrow">Area privata · ${cloudEnabled ? "archivio cifrato" : "accesso richiesto"}</span>
        <h2>${cloudEnabled ? "Il tuo archivio, illeggibile per la piattaforma." : "I PDF scaricati restano sul tuo dispositivo."}</h2>
        <p>${cloudEnabled
          ? "Il browser cifra ogni preventivo prima del salvataggio online. La piattaforma conserva soltanto contenuti illeggibili."
          : "Puoi calcolare e scaricare il PDF senza account. Accedi soltanto se vuoi ritrovarlo online nella tua area privata."}</p>
      </div>
      <span class="vault-status" data-vault-status></span>
    </div>
    <div class="vault-warning" role="note">
      <strong>${cloudEnabled ? "Archivio cifrato: conserva il codice di emergenza." : "Nessun salvataggio online attivo."}</strong>
      <span>${cloudEnabled
        ? "La piattaforma non conosce la password dell’archivio né il codice di emergenza e non può leggere i documenti."
        : "Il PDF viene comunque scaricato sul dispositivo. Per conservarlo anche online devi prima accedere all’area privata."}</span>
    </div>
    <div class="vault-live" data-vault-live role="status" aria-live="polite" hidden></div>
    <div data-vault-view></div>`;
}

function activationView(cloudEnabled: boolean): string {
  return `
    <div class="vault-grid vault-grid--activation">
      <form class="vault-card vault-form" data-vault-form="create">
        <span class="vault-card__number">01</span>
        <h3>${cloudEnabled ? "Attiva il tuo archivio privato" : "Crea un archivio temporaneo"}</h3>
        <p>${cloudEnabled
          ? "Scegli una password dedicata per proteggere i preventivi salvati online. È diversa dall’accesso con Google o email e rimane soltanto tua."
          : "Scegli una password dedicata. Questa prova rimane soltanto nella memoria temporanea del browser."}</p>
        <label>Password dell’archivio
          <input name="password" type="password" minlength="12" autocomplete="new-password" required />
        </label>
        <label>Ripeti la password
          <input name="confirmation" type="password" minlength="12" autocomplete="new-password" required />
        </label>
        <button type="submit">${cloudEnabled ? "Attiva archivio protetto" : "Crea archivio temporaneo"}</button>
      </form>
      <div class="vault-card vault-explainer">
        <span class="vault-card__number">Come funziona</span>
        <ol>
          <li><strong>Accedi con Google o email.</strong><span>Il login identifica il proprietario dell’archivio.</span></li>
          <li><strong>Apri i PDF con la password.</strong><span>È la modalità normale per consultare i preventivi salvati.</span></li>
          <li><strong>Usa il codice solo in emergenza.</strong><span>Serve esclusivamente se dimentichi la password.</span></li>
        </ol>
      </div>
    </div>`;
}

function signedOutView(): string {
  return `
    <div class="vault-grid vault-grid--activation">
      <div class="vault-card vault-form">
        <span class="vault-card__number">Opzionale</span>
        <h3>Vuoi ritrovare i preventivi online?</h3>
        <p>Accedi con Google o email e attiva il tuo archivio privato. Senza accesso puoi continuare a creare e scaricare normalmente i PDF.</p>
        <button type="button" data-vault-auth>Accedi all’area privata</button>
      </div>
      <div class="vault-card vault-explainer">
        <span class="vault-card__number">In breve</span>
        <ol>
          <li><strong>Il download non richiede un account.</strong><span>Il PDF resta sul dispositivo.</span></li>
          <li><strong>Il login abilita l’archivio online.</strong><span>Ogni utente vede soltanto i propri preventivi.</span></li>
          <li><strong>La protezione si configura dopo l’accesso.</strong><span>Password e codice di emergenza riguardano solo l’archivio privato.</span></li>
        </ol>
      </div>
    </div>`;
}

function recoveryView(): string {
  return `
    <div class="vault-recovery">
      <div class="vault-recovery__icon" aria-hidden="true">⌁</div>
      <div>
        <span class="eyebrow">Passaggio irripetibile</span>
        <h3>Conserva il codice di emergenza</h3>
        <p>Ti servirà esclusivamente se dimentichi la password dell’archivio: non è necessario per aprire normalmente i PDF. Dopo la conferma verrà rimosso dalla pagina e non sarà inviato per email.</p>
        <code class="vault-recovery__code" data-recovery-code></code>
        <div class="vault-recovery__actions">
          <button type="button" class="vault-button vault-button--secondary" data-copy-recovery>Copia codice di emergenza</button>
          <label class="check"><input type="checkbox" data-recovery-confirm /><span>Confermo di aver conservato il codice in un luogo sicuro.</span></label>
          <button type="button" class="vault-button" data-complete-recovery disabled>Continua nell’archivio</button>
        </div>
        <p><strong>Importante:</strong> se perdi sia la password sia questo codice, nemmeno l’assistenza potrà recuperare i documenti.</p>
      </div>
    </div>`;
}

function lockedView(): string {
  return `
    <div class="vault-grid">
      <form class="vault-card vault-form" data-vault-form="unlock">
        <span class="vault-card__number">Password</span>
        <h3>Sblocca l’archivio</h3>
        <p>Usa la password scelta quando hai attivato l’archivio. Il codice di emergenza non serve per l’accesso normale.</p>
        <label>Password dell’archivio
          <input name="password" type="password" autocomplete="current-password" required />
        </label>
        <button type="submit">Sblocca</button>
      </form>
      <form class="vault-card vault-form" data-vault-form="recover">
        <span class="vault-card__number">Recupero</span>
        <h3>Hai dimenticato la password?</h3>
        <p>Usa questa procedura soltanto se hai dimenticato la password.</p>
        <label>Codice di emergenza
          <input name="recovery" type="password" autocomplete="off" required />
        </label>
        <label>Nuova password
          <input name="password" type="password" minlength="12" autocomplete="new-password" required />
        </label>
        <label>Ripeti la nuova password
          <input name="confirmation" type="password" minlength="12" autocomplete="new-password" required />
        </label>
        <button type="submit">Reimposta e sblocca</button>
      </form>
    </div>`;
}

function unlockedView(
  documentCount: number,
  hasPendingQuote: boolean,
  cloudEnabled: boolean,
): string {
  return `
    <div class="vault-toolbar">
      <div><strong>${documentCount} preventiv${documentCount === 1 ? "o" : "i"} cifrat${documentCount === 1 ? "o" : "i"}</strong><span>${cloudEnabled
        ? "Gli artefatti sono persistenti ma restano illeggibili senza la password del vault."
        : "Nessun contenuto è stato salvato fuori da questa pagina."}</span></div>
      <div>
        <button type="button" class="vault-button vault-button--secondary" data-lock-vault>Blocca archivio</button>
        <button type="button" class="vault-button" data-save-current ${hasPendingQuote ? "" : "disabled"}>${cloudEnabled ? "Salva PDF nell’area privata" : "Salva PDF nell’archivio temporaneo"}</button>
      </div>
    </div>
    <div class="vault-unlocked-grid">
      <section class="vault-documents" aria-labelledby="vault-documents-title">
        <div class="vault-subheading"><div><span class="eyebrow">Cronologia ${cloudEnabled ? "cifrata" : "locale"}</span><h3 id="vault-documents-title">Preventivi salvati</h3></div><span>PDF · Modificabile · Indice</span></div>
        <div data-document-list></div>
      </section>
      <aside class="vault-preview" data-vault-preview>
        <span class="eyebrow">Anteprima decifrata</span>
        <h3>Seleziona un preventivo</h3>
        <p>Il contenuto leggibile appare soltanto dopo lo sblocco e la decifratura locale.</p>
      </aside>
    </div>
    <details class="vault-security-settings">
      <summary>Impostazioni di sicurezza dell’archivio</summary>
      <form class="vault-form vault-form--inline" data-vault-form="change-password">
        <label>Password attuale<input name="current" type="password" autocomplete="current-password" required /></label>
        <label>Nuova password<input name="password" type="password" minlength="12" autocomplete="new-password" required /></label>
        <label>Ripeti nuova password<input name="confirmation" type="password" minlength="12" autocomplete="new-password" required /></label>
        <button type="submit">Cambia password</button>
      </form>
    </details>`;
}

function renderDocumentList(root: HTMLElement, state: VaultLabState): void {
  const list = root.querySelector<HTMLElement>("[data-document-list]");
  if (list === null) return;
  if (state.documents.length === 0) {
    list.innerHTML = `
      <div class="vault-empty">
        <span aria-hidden="true">◇</span>
        <strong>Archivio ancora vuoto</strong>
        <p>Genera un PDF dal preventivo, quindi salvalo qui: PDF, dati modificabili e indice verranno cifrati separatamente.</p>
      </div>`;
    return;
  }
  const fragment = document.createDocumentFragment();
  state.documents.forEach((item, index) => {
    const row = document.createElement("article");
    row.className = "vault-document";
    const identity = document.createElement("div");
    identity.innerHTML = `<span aria-hidden="true">${String(index + 1).padStart(2, "0")}</span><p><strong>Preventivo cifrato</strong><small></small></p>`;
    const small = identity.querySelector("small");
    if (small !== null) small.textContent = `${formatTimestamp(item.createdAt)} · ID ${shortId(item.documentId)}`;
    const artifacts = document.createElement("span");
    artifacts.className = "vault-document__artifacts";
    artifacts.textContent = item.artifacts.length === 0 && state.cloud !== null
      ? "3/3 · cloud cifrato"
      : `${item.artifacts.length}/3 cifrati`;
    const open = document.createElement("button");
    open.type = "button";
    open.textContent = "Apri";
    open.dataset.openDocument = item.documentId;
    row.append(identity, artifacts, open);
    fragment.append(row);
  });
  list.replaceChildren(fragment);
}

function renderPreview(
  root: HTMLElement,
  preview: ArchivedQuotePreview | null,
  selectedDocumentId: string | null,
): void {
  const container = root.querySelector<HTMLElement>("[data-vault-preview]");
  if (container === null || preview === null) return;
  container.innerHTML = `
    <span class="eyebrow">Anteprima decifrata</span>
    <span class="vault-preview__seal">Aperto soltanto in memoria</span>
    <h3 data-preview-title></h3>
    <dl><div><dt>Committente</dt><dd data-preview-client></dd></div><div><dt>Totale</dt><dd data-preview-total></dd></div></dl>
    <button type="button" class="vault-button" data-download-document="${selectedDocumentId ?? ""}">Scarica il PDF decifrato</button>
    <p>Il contenuto leggibile esiste soltanto nella memoria della pagina. Bloccando il vault viene rimosso dall’interfaccia.</p>`;
  const title = container.querySelector<HTMLElement>("[data-preview-title]");
  const client = container.querySelector<HTMLElement>("[data-preview-client]");
  const total = container.querySelector<HTMLElement>("[data-preview-total]");
  if (title !== null) title.textContent = preview.projectTitle;
  if (client !== null) client.textContent = preview.clientLabel;
  if (total !== null) total.textContent = preview.total;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function parseArchivedQuoteSnapshot(value: unknown): ArchivedQuoteSnapshotV1 {
  if (!isRecord(value)) throw new Error("Snapshot del preventivo non valido");
  if (value.format !== QUOTE_ARCHIVE_FORMAT || value.version !== QUOTE_ARCHIVE_VERSION) {
    throw new Error("Formato dello snapshot non supportato");
  }
  if (typeof value.filename !== "string" || !isSafeArchiveFilename(value.filename)) {
    throw new Error("Nome del PDF archiviato non valido");
  }
  if (!isRecord(value.quote)) throw new Error("Preventivo archiviato non valido");
  const quote = value.quote;
  const releaseMetadataValid = quote.professionalUseAllowed === false
    || (
      quote.professionalUseAllowed === true
      && quote.certifiedEquoCompenso === false
    );
  if (
    !releaseMetadataValid
    || !new Set([QUOTE_DOCUMENT_SCHEMA_VERSION, "2.7", "2.7.0"]).has(String(quote.schemaVersion))
    || !isRecord(quote.draft)
    || !isRecord(quote.calculation)
    || !isRecord(quote.financials)
    || !isRecord(quote.financials.totals)
    || typeof quote.draft.clientName !== "string"
    || typeof quote.draft.projectSubject !== "string"
    || typeof quote.draft.issueDate !== "string"
    || typeof quote.financials.totals.grandTotal !== "string"
    || typeof quote.calculation.resultSha256 !== "string"
    || !/^[a-f0-9]{64}$/i.test(quote.calculation.resultSha256)
  ) {
    throw new Error("Contenuto del preventivo archiviato non valido");
  }
  return value as unknown as ArchivedQuoteSnapshotV1;
}

function isSafeArchiveFilename(value: string): boolean {
  return value.length <= 200 && /^[a-z0-9][a-z0-9._-]*\.pdf$/i.test(value);
}

function assertPdf(bytes: Uint8Array): void {
  const signature = decoder.decode(bytes.subarray(0, 5));
  if (bytes.byteLength < 8 || signature !== "%PDF-") {
    throw new Error("Il documento archiviato non è un PDF valido");
  }
}

function quotePreview(snapshot: ArchivedQuoteSnapshotV1): ArchivedQuotePreview {
  const [totalInteger = "0", totalDecimals = "00"] = snapshot.quote.financials.totals.grandTotal
    .split(".");
  const formattedTotal = `${totalInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${totalDecimals.padEnd(2, "0")}`;
  return {
    clientLabel: snapshot.quote.draft.clientName,
    projectTitle: snapshot.quote.draft.projectSubject,
    total: `${formattedTotal} €`,
    filename: snapshot.filename,
    issueDate: snapshot.quote.draft.issueDate,
    resultSha256: snapshot.quote.calculation.resultSha256,
  };
}

interface PreparedArchivedQuotePlaintexts {
  readonly artifacts: PlaintextVaultArtifactSet;
  clear(): void;
}

function prepareArchivedQuotePlaintexts(
  input: VaultQuoteArchiveInput,
): PreparedArchivedQuotePlaintexts {
  if (!isSafeArchiveFilename(input.filename)) {
    throw new Error("Nome del PDF da archiviare non valido");
  }
  assertPdf(input.pdfBytes);
  const snapshot: ArchivedQuoteSnapshotV1 = {
    format: QUOTE_ARCHIVE_FORMAT,
    version: QUOTE_ARCHIVE_VERSION,
    filename: input.filename,
    quote: input.quote,
  };
  const pdfPlaintext = Uint8Array.from(input.pdfBytes);
  const snapshotPlaintext = encoder.encode(JSON.stringify(snapshot));
  const indexPlaintext = encoder.encode(JSON.stringify({
    terms: [
      input.quote.draft.clientName,
      input.quote.draft.projectSubject,
      input.quote.draft.issueDate,
    ],
  }));
  return {
    artifacts: {
      pdf: { mimeType: "application/pdf", plaintext: pdfPlaintext },
      editable_snapshot: { mimeType: "application/json", plaintext: snapshotPlaintext },
      search_index: { mimeType: "application/json", plaintext: indexPlaintext },
    },
    clear() {
      pdfPlaintext.fill(0);
      snapshotPlaintext.fill(0);
      indexPlaintext.fill(0);
    },
  };
}

export async function createArchivedQuoteDocument(
  session: VaultSession,
  input: VaultQuoteArchiveInput,
): Promise<VaultLabDocument> {
  const documentId = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const prepared = prepareArchivedQuotePlaintexts(input);
  try {
    const [pdf, editableSnapshot, searchIndex] = await Promise.all([
      encryptVaultArtifact(session, {
        documentId,
        artifactKind: "pdf",
        revision: 1,
        mimeType: prepared.artifacts.pdf.mimeType,
        plaintext: prepared.artifacts.pdf.plaintext,
        createdAt,
      }),
      encryptVaultArtifact(session, {
        documentId,
        artifactKind: "editable_snapshot",
        revision: 1,
        mimeType: prepared.artifacts.editable_snapshot.mimeType,
        plaintext: prepared.artifacts.editable_snapshot.plaintext,
        createdAt,
      }),
      encryptVaultArtifact(session, {
        documentId,
        artifactKind: "search_index",
        revision: 1,
        mimeType: prepared.artifacts.search_index.mimeType,
        plaintext: prepared.artifacts.search_index.plaintext,
        createdAt,
      }),
    ]);
    return { documentId, createdAt, artifacts: [pdf, editableSnapshot, searchIndex] };
  } finally {
    prepared.clear();
  }
}

async function openArchivedQuoteSnapshot(
  session: VaultSession,
  item: VaultLabDocument,
): Promise<ArchivedQuoteSnapshotV1> {
  const artifact = item.artifacts.find((candidate) => candidate.artifactKind === "editable_snapshot");
  if (artifact === undefined) throw new Error("Snapshot cifrato mancante");
  const plaintext = await decryptVaultArtifact(session, artifact);
  try {
    return parseArchivedQuoteSnapshot(JSON.parse(decoder.decode(plaintext)) as unknown);
  } catch (error) {
    if (error instanceof SyntaxError) throw new Error("Snapshot cifrato non leggibile");
    throw error;
  } finally {
    plaintext.fill(0);
  }
}

export async function openArchivedQuoteDocument(
  session: VaultSession,
  item: VaultLabDocument,
): Promise<ArchivedQuotePreview> {
  return quotePreview(await openArchivedQuoteSnapshot(session, item));
}

export async function decryptArchivedQuotePdf(
  session: VaultSession,
  item: VaultLabDocument,
): Promise<DecryptedArchivedQuotePdf> {
  const artifact = item.artifacts.find((candidate) => candidate.artifactKind === "pdf");
  if (artifact === undefined) throw new Error("PDF cifrato mancante");
  const [snapshot, bytes] = await Promise.all([
    openArchivedQuoteSnapshot(session, item),
    decryptVaultArtifact(session, artifact),
  ]);
  try {
    assertPdf(bytes);
    return { bytes, filename: snapshot.filename };
  } catch (error) {
    bytes.fill(0);
    throw error;
  }
}

function downloadArchivedPdf(artifact: DecryptedArchivedQuotePdf): void {
  const blob = new Blob([Uint8Array.from(artifact.bytes).buffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  anchor.hidden = true;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function createDemoDocument(session: VaultSession): Promise<VaultLabDocument> {
  const documentId = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const preview: DemoPreview = {
    clientLabel: "Cliente dimostrativo",
    projectTitle: "Riqualificazione edificio campione",
    total: "7.631,20 €",
  };
  const [pdf, snapshot, searchIndex] = await Promise.all([
    encryptVaultArtifact(session, {
      documentId,
      artifactKind: "pdf",
      revision: 1,
      mimeType: "application/pdf",
      plaintext: encoder.encode("%PDF-DEMO\nPreventivo sintetico senza validità\n%%EOF"),
      createdAt,
    }),
    encryptVaultArtifact(session, {
      documentId,
      artifactKind: "editable_snapshot",
      revision: 1,
      mimeType: "application/json",
      plaintext: encoder.encode(JSON.stringify(preview)),
      createdAt,
    }),
    encryptVaultArtifact(session, {
      documentId,
      artifactKind: "search_index",
      revision: 1,
      mimeType: "application/json",
      plaintext: encoder.encode(JSON.stringify({
        terms: [preview.clientLabel, preview.projectTitle],
      })),
      createdAt,
    }),
  ]);
  return { documentId, createdAt, artifacts: [pdf, snapshot, searchIndex] };
}

export async function openDemoDocument(
  session: VaultSession,
  item: VaultLabDocument,
): Promise<DemoPreview> {
  const snapshot = item.artifacts.find((artifact) => artifact.artifactKind === "editable_snapshot");
  if (snapshot === undefined) throw new Error("Snapshot dimostrativo mancante");
  const plaintext = await decryptVaultArtifact(session, snapshot);
  try {
    return JSON.parse(decoder.decode(plaintext)) as DemoPreview;
  } finally {
    plaintext.fill(0);
  }
}

export function mountVaultLab(root: HTMLElement): VaultLabController {
  const state: VaultLabState = {
    manifest: null,
    session: null,
    recoveryCode: null,
    recoveryConfirmed: false,
    documents: [],
    preview: null,
    selectedDocumentId: null,
    pendingQuote: null,
    cloud: null,
    remoteRevisions: new Map(),
    message: null,
    error: null,
    busy: false,
  };
  let cloudGeneration = 0;

  const run = async (operation: () => Promise<void>): Promise<void> => {
    state.busy = true;
    state.message = null;
    state.error = null;
    render();
    try {
      await operation();
    } catch (error) {
      state.error = error instanceof Error ? error.message : "Operazione non riuscita";
    } finally {
      state.busy = false;
      render();
    }
  };

  const bindForm = (
    name: string,
    handler: (form: HTMLFormElement) => Promise<void>,
  ): void => {
    root.querySelector<HTMLFormElement>(`[data-vault-form='${name}']`)
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        void run(() => handler(event.currentTarget as HTMLFormElement));
      });
  };

  const resolveDocument = async (item: VaultLabDocument): Promise<VaultLabDocument> => {
    if (item.artifacts.length > 0) return item;
    const cloud = state.cloud;
    const generation = cloudGeneration;
    if (cloud === null) throw new Error("Documento cifrato non disponibile");
    const revision = state.remoteRevisions.get(item.documentId);
    if (revision === undefined) throw new Error("Revisione cifrata non disponibile");
    const artifacts = await loadEncryptedVaultRevision(
      cloud.remote,
      item.documentId,
      revision,
    );
    if (generation !== cloudGeneration) {
      throw new Error("Account cambiato durante il download cifrato");
    }
    const hydrated: VaultLabDocument = {
      ...item,
      artifacts,
    };
    state.documents = state.documents.map((candidate) => (
      candidate.documentId === hydrated.documentId ? hydrated : candidate
    ));
    return hydrated;
  };

  const render = (): void => {
    root.innerHTML = baseShell(state.cloud !== null);
    const view = root.querySelector<HTMLElement>("[data-vault-view]");
    if (view === null) return;
    if (state.cloud === null) view.innerHTML = signedOutView();
    else if (state.manifest === null) view.innerHTML = activationView(true);
    else if (!state.recoveryConfirmed) view.innerHTML = recoveryView();
    else if (state.session === null) view.innerHTML = lockedView();
    else view.innerHTML = unlockedView(
      state.documents.length,
      state.pendingQuote !== null,
      state.cloud !== null,
    );

    renderStatus(root, state);
    renderDocumentList(root, state);
    renderPreview(root, state.preview, state.selectedDocumentId);
    root.querySelectorAll<HTMLButtonElement>("button").forEach((button) => {
      button.disabled ||= state.busy;
    });

    root.querySelector<HTMLButtonElement>("[data-vault-auth]")?.addEventListener("click", () => {
      document.querySelector<HTMLButtonElement>("#auth-trigger")?.click();
    });

    bindForm("create", async (form) => {
      const password = getPassword(form, "password");
      if (password !== getPassword(form, "confirmation")) {
        throw new Error("Le due password non coincidono");
      }
      const created = await createVault(password);
      if (state.cloud !== null) {
        await state.cloud.remote.createVaultManifest(state.cloud.userId, created.manifest);
      }
      state.manifest = created.manifest;
      state.session = created.session;
      state.recoveryCode = created.recoveryCode;
      state.message = state.cloud === null
        ? "Vault temporaneo creato nel browser."
        : "Archivio privato attivato. Password e codice di emergenza non sono stati inviati alla piattaforma.";
    });

    if (state.recoveryCode !== null) {
      const recoveryCode = root.querySelector<HTMLElement>("[data-recovery-code]");
      if (recoveryCode !== null) recoveryCode.textContent = state.recoveryCode;
    }
    const recoveryCheck = root.querySelector<HTMLInputElement>("[data-recovery-confirm]");
    const recoveryComplete = root.querySelector<HTMLButtonElement>("[data-complete-recovery]");
    recoveryCheck?.addEventListener("change", () => {
      if (recoveryComplete !== null) recoveryComplete.disabled = !recoveryCheck.checked;
    });
    root.querySelector<HTMLButtonElement>("[data-copy-recovery]")?.addEventListener("click", () => {
      if (state.recoveryCode === null) return;
      void navigator.clipboard.writeText(state.recoveryCode).then(() => {
        state.message = "Codice copiato. Proteggi anche gli appunti del dispositivo.";
        renderStatus(root, state);
      }).catch(() => {
        state.error = "Copia automatica non disponibile: seleziona il codice manualmente.";
        renderStatus(root, state);
      });
    });
    recoveryComplete?.addEventListener("click", () => {
      if (recoveryCheck?.checked !== true) return;
      state.recoveryConfirmed = true;
      state.recoveryCode = null;
      state.message = "Codice rimosso dalla pagina. Archivio sbloccato.";
      render();
    });

    bindForm("unlock", async (form) => {
      if (state.manifest === null) throw new Error("Manifest del vault mancante");
      state.session = await unlockVaultWithPassword(
        state.manifest,
        getPassword(form, "password"),
      );
      state.message = "Archivio sbloccato soltanto per questa sessione.";
    });

    bindForm("recover", async (form) => {
      if (state.manifest === null) throw new Error("Manifest del vault mancante");
      const password = getPassword(form, "password");
      if (password !== getPassword(form, "confirmation")) {
        throw new Error("Le due nuove password non coincidono");
      }
      const updatedManifest = await resetVaultPasswordWithRecoveryCode(
        state.manifest,
        getPassword(form, "recovery"),
        password,
      );
      if (state.cloud !== null) {
        await state.cloud.remote.updateVaultManifest(updatedManifest);
      }
      state.manifest = updatedManifest;
      state.session = await unlockVaultWithPassword(state.manifest, password);
      state.message = "Password reimpostata e archivio sbloccato.";
    });

    root.querySelector<HTMLButtonElement>("[data-lock-vault]")?.addEventListener("click", () => {
      state.session = null;
      state.preview = null;
      state.selectedDocumentId = null;
      state.message = "Chiave rimossa dallo stato dell’applicazione.";
      render();
    });

    root.querySelector<HTMLButtonElement>("[data-save-current]")?.addEventListener("click", () => {
      void run(async () => {
        if (state.session === null) throw new Error("Vault bloccato");
        if (state.pendingQuote === null) throw new Error("Genera prima un PDF dal preventivo");
        const session = state.session;
        const cloud = state.cloud;
        const generation = cloudGeneration;
        const pending = state.pendingQuote;
        if (cloud === null) {
          state.documents.unshift(await createArchivedQuoteDocument(session, pending));
          state.message = "PDF, snapshot modificabile e indice cifrati esclusivamente in memoria.";
        } else {
          const prepared = prepareArchivedQuotePlaintexts(pending);
          const createdAt = new Date().toISOString();
          try {
            const saved = await saveNewVaultDocument(cloud.remote, {
              userId: cloud.userId,
              session,
              artifacts: prepared.artifacts,
              createdAt,
            });
            if (generation !== cloudGeneration) {
              throw new Error("Account cambiato durante il salvataggio cifrato");
            }
            state.remoteRevisions.set(saved.documentId, saved.revision);
            state.documents.unshift({
              documentId: saved.documentId,
              createdAt,
              artifacts: [],
            });
            state.message = "Preventivo cifrato nel browser e salvato nel cloud privato.";
          } finally {
            prepared.clear();
          }
        }
        pending.pdfBytes.fill(0);
        state.pendingQuote = null;
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-open-document]").forEach((button) => {
      button.addEventListener("click", () => {
        void run(async () => {
          if (state.session === null) throw new Error("Vault bloccato");
          const storedItem = state.documents.find(
            (documentItem) => documentItem.documentId === button.dataset.openDocument,
          );
          if (storedItem === undefined) throw new Error("Documento cifrato non trovato");
          const item = await resolveDocument(storedItem);
          state.preview = await openArchivedQuoteDocument(state.session, item);
          state.selectedDocumentId = item.documentId;
          state.message = "Snapshot decifrato localmente.";
        });
      });
    });

    root.querySelector<HTMLButtonElement>("[data-download-document]")?.addEventListener("click", () => {
      void run(async () => {
        if (state.session === null) throw new Error("Vault bloccato");
        const storedItem = state.documents.find(
          (documentItem) => documentItem.documentId === state.selectedDocumentId,
        );
        if (storedItem === undefined) throw new Error("Documento cifrato non trovato");
        const item = await resolveDocument(storedItem);
        const artifact = await decryptArchivedQuotePdf(state.session, item);
        try {
          downloadArchivedPdf(artifact);
          state.message = "PDF decifrato e scaricato esclusivamente sul dispositivo.";
        } finally {
          artifact.bytes.fill(0);
        }
      });
    });

    bindForm("change-password", async (form) => {
      if (state.manifest === null) throw new Error("Manifest del vault mancante");
      const password = getPassword(form, "password");
      if (password !== getPassword(form, "confirmation")) {
        throw new Error("Le due nuove password non coincidono");
      }
      const updatedManifest = await changeVaultPassword(
        state.manifest,
        getPassword(form, "current"),
        password,
      );
      if (state.cloud !== null) {
        await state.cloud.remote.updateVaultManifest(updatedManifest);
      }
      state.manifest = updatedManifest;
      state.message = "Password cambiata senza ricifrare i documenti.";
    });
  };

  render();
  return {
    setPendingQuote(input) {
      state.pendingQuote?.pdfBytes.fill(0);
      state.pendingQuote = input === null
        ? null
        : {
            pdfBytes: Uint8Array.from(input.pdfBytes),
            filename: input.filename,
            quote: input.quote,
          };
      state.message = input === null
        ? null
        : "PDF scaricato sul dispositivo. Se vuoi ritrovarlo online, sblocca l’archivio e scegli “Salva PDF nell’area privata”.";
      state.error = null;
      render();
    },
    setCloudContext(context) {
      const generation = ++cloudGeneration;
      state.cloud = context;
      state.manifest = null;
      state.session = null;
      state.recoveryCode = null;
      state.recoveryConfirmed = false;
      state.documents = [];
      state.remoteRevisions.clear();
      state.preview = null;
      state.selectedDocumentId = null;
      if (context === null) {
        state.message = "Non hai effettuato l’accesso: nessun PDF sarà salvato online.";
        state.error = null;
        render();
        return;
      }
      void run(async () => {
        const manifest = await context.remote.getVaultManifest();
        if (generation !== cloudGeneration) return;
        state.manifest = manifest;
        if (manifest === null) {
          state.message = "Account collegato: crea il vault cifrato per attivare la cronologia persistente.";
          return;
        }
        state.recoveryConfirmed = true;
        const revisions = await context.remote.listLatestReadyRevisions();
        if (generation !== cloudGeneration) return;
        state.documents = revisions.map((revision) => ({
          documentId: revision.documentId,
          createdAt: revision.createdAt,
          artifacts: [],
        }));
        revisions.forEach((revision) => {
          state.remoteRevisions.set(revision.documentId, revision.revision);
        });
        state.message = `${revisions.length} preventiv${revisions.length === 1 ? "o" : "i"} cifrat${revisions.length === 1 ? "o" : "i"} disponibil${revisions.length === 1 ? "e" : "i"}. Inserisci la password del vault per aprire.`;
      });
    },
  };
}
